import React, { Component, ReactNode } from 'react';
import { Language } from '../store/languageStore';
import { translate } from '../i18n/translations';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// Helper function to get language from localStorage (same logic as languageStore)
const getStoredLanguage = (): Language => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = localStorage.getItem('hours-language-storage');
      if (stored) {
        const lang = JSON.parse(stored) as Language;
        // Validate language code
        if (['he', 'en', 'ru', 'si', 'uz', 'ro'].includes(lang)) {
          return lang;
        }
      }
    }
  } catch (e) {
    console.error('Failed to load language from storage', e);
  }
  return 'he'; // Default to Hebrew
};

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error to console (in production, you might want to send this to a logging service)
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReload = () => {
    // Reload the page
    window.location.reload();
  };

  handleGoBack = () => {
    // Navigate back in history
    window.history.back();
  };

  render() {
    if (this.state.hasError) {
      // Get current language from localStorage
      const language = getStoredLanguage();

      // Get translated messages
      const title = translate('error.title', language);
      const message = translate('error.message', language);
      const reloadText = translate('error.reload', language);
      const goBackText = translate('error.goBack', language);

      // Determine text direction based on language
      const isRtl = language === 'he';
      const dir = isRtl ? 'rtl' : 'ltr';

      return (
        <div
          dir={dir}
          className="min-h-screen bg-gray-50 flex items-center justify-center p-4"
        >
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
            <div className="mb-6">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                <svg
                  className="h-8 w-8 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{title}</h1>
              <p className="text-gray-600 mb-6">{message}</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleReload}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                {reloadText}
              </button>
              <button
                onClick={this.handleGoBack}
                className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
              >
                {goBackText}
              </button>
            </div>

            {/* Show error details in development only */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-gray-500 mb-2">
                  Error Details (Development Only)
                </summary>
                <pre className="text-xs bg-gray-100 p-3 rounded overflow-auto max-h-40">
                  {this.state.error.toString()}
                  {'\n'}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

